import { Draft } from 'immer';
import { assertProjectionIdentities } from './assertProjectionIdentities';
import { PolicyProjectionStrategy } from './strategyTypes';

// Supplied only for joint policies. The projection then runs over the merged,
// claimant-tagged stream, resolving each row's claimant client + phase plan and
// threading the shared policy-year anchor into the strategy. The session client
// (passed as `mutableClient`) serves 'client' rows; `partnerClient` serves
// 'partner' rows. Built by `buildJointMonthlyCareInputs` + the joint orchestration.
export interface JointProjectionContext {
  monthlyCareInputs: MonthlyCareInput[];
  partnerClient: Draft<Client>;
  // Integer policy year at monthIndex 0 — i.e. Jan 1 of the earliest spouse's
  // onset year (the calendar origin `buildJointMonthlyCareInputs` measures every
  // spouse's offset from). `resolveRowPolicyYear` adds `floor(monthIndex/12)` so
  // each spouse lands on its true calendar policy year by integer math.
  anchorPolicyYear: number;
}

export interface ProcessClaimProjectionOptions {
  // If true, every month is checked against assertProjectionIdentities and a
  // failure throws. Defaults to true — strategies are bugs if identities
  // fail, and we want them to surface loudly during shadow mode.
  assertIdentities?: boolean;
  // Present only for joint policies — see JointProjectionContext.
  joint?: JointProjectionContext;
}

export interface ProcessClaimProjectionResult<TState = unknown, TDeltas = unknown> {
  projection: ClaimProjection<TState, TDeltas>;
  finalDeathBenefit: number;
  closingState: MonthlyState<TState>;
}

export function processClaimProjection<TState, TDeltas>(
  mutableClient: Draft<Client>,
  ltcPolicy: PolicyFundingSource,
  strategy: PolicyProjectionStrategy<TState, TDeltas>,
  options: ProcessClaimProjectionOptions = {},
): ProcessClaimProjectionResult<TState, TDeltas> {
  const { assertIdentities = true, joint } = options;

  // Single-life draws the stream + plans from the session client; joint draws
  // the merged stream from the context and resolves plans per claimant.
  const monthlyCareInputs = joint
    ? joint.monthlyCareInputs
    : mutableClient.projectionInputs.monthlyCareInputs;

  const clientPhaseLookup = new Map(
    mutableClient.projectionInputs.phaseCarePlans.map(plan => [plan.carePhase, plan]),
  );
  const partnerPhaseLookup = joint
    ? new Map(joint.partnerClient.projectionInputs.phaseCarePlans.map(plan => [plan.carePhase, plan]))
    : null;

  const initialState = strategy.initClaimState(mutableClient, ltcPolicy, joint?.anchorPolicyYear);
  const terms = strategy.buildClaimTerms(mutableClient, ltcPolicy);

  const yearSnapshots: YearSnapshot[] = [];
  const yearSnapshotIndexByPolicyYear = new Map<number, number>();

  let openState = initialState;
  const months: MonthlyEntry<TState, TDeltas>[] = [];
  // Last emitted row per claimant ('client' / 'partner', or '__single__' for
  // single-life). Joint streams interleave the spouses when their care overlaps,
  // so the global previous row is often the OTHER spouse — milestone transition
  // detection needs the prior row of the SAME claimant instead.
  const lastMonthByClaimant = new Map<string, MonthlyEntry<TState, TDeltas>>();

  for (const monthlyCareInput of monthlyCareInputs) {
    const isPartnerRow = monthlyCareInput.claimant === 'partner';
    const rowClient = isPartnerRow && joint ? joint.partnerClient : mutableClient;
    const phaseLookup = isPartnerRow && partnerPhaseLookup ? partnerPhaseLookup : clientPhaseLookup;
    const phaseCarePlan = phaseLookup.get(monthlyCareInput.carePhase);
    if (!phaseCarePlan) continue;

    const claimantKey = monthlyCareInput.claimant ?? '__single__';
    const priorMonth = months.length > 0 ? months[months.length - 1] : null;
    const priorClaimantMonth = lastMonthByClaimant.get(claimantKey) ?? null;
    const month = strategy.computeMonth({
      client: rowClient,
      ltcPolicy,
      phaseCarePlan,
      monthlyCareInput,
      openState,
      priorMonth,
      priorClaimantMonth,
      jointAnchorPolicyYear: joint?.anchorPolicyYear,
    });

    if (assertIdentities) {
      assertProjectionIdentities(month);
    }

    // Snapshot the policy year on first sight. For joint, snapshots (and the
    // claim terms above) are policy-level metadata — rates, caps, pool sizes —
    // so they're intentionally sourced from the session client; per-claimant
    // facts come from `month` instead.
    const { policyYear, calendarYearOffset } = month.context;
    if (!yearSnapshotIndexByPolicyYear.has(policyYear)) {
      const snapshot = strategy.buildYearSnapshot({
        client: mutableClient,
        ltcPolicy,
        policyYear,
        calendarYearOffset,
      });
      yearSnapshotIndexByPolicyYear.set(policyYear, yearSnapshots.length);
      yearSnapshots.push(snapshot);
    }

    months.push(month);
    lastMonthByClaimant.set(claimantKey, month);
    openState = month.state;
  }

  const finalDeathBenefit = strategy.finalizeDeathBenefit({
    ltcPolicy,
    closingState: openState,
  });

  return {
    projection: { terms, yearSnapshots, months },
    finalDeathBenefit,
    closingState: openState,
  };
}
